import { z } from 'zod';

export const RoomCodeSchema = z.string().trim().toUpperCase().regex(/^[23456789ABCDEFGHJKMNPQRSTUVWXYZ]{4}$/);
export const NicknameSchema = z.string().trim().min(1).max(20).refine(
  (value) => !/[\u0000-\u001f\u007f]/.test(value),
  { message: 'Nickname contains unsupported control characters' },
);
export const PlayerIdSchema = z.string().uuid();
export const CommandIdSchema = z.string().uuid();
export const ReconnectTokenSchema = z.string().min(32).max(256);
export const IconIdSchema = z.string().regex(/^p[12]-\d{3}$/);
export const MarkerColorSchema = z.enum(['green', 'blue', 'red', 'yellow', 'black']);
export const MarkerKindSchema = z.enum(['pawn', 'cube']);
export const MarkerIdSchema = z.string().regex(/^(green|blue|red|yellow|black)-(pawn|cube-\d{1,2})$/);

export const MarkerPlacementSchema = z.object({
  id: MarkerIdSchema,
  color: MarkerColorSchema,
  kind: MarkerKindSchema,
  iconId: IconIdSchema,
  order: z.number().int().positive(),
}).strict();
export type MarkerPlacement = z.infer<typeof MarkerPlacementSchema>;
export type MarkerColor = z.infer<typeof MarkerColorSchema>;
export type MarkerKind = z.infer<typeof MarkerKindSchema>;

export const BoardActivitySchema = z.object({
  id: z.number().int().positive(),
  action: z.enum(['added', 'moved', 'removed', 'cleared']),
  actorPlayerId: PlayerIdSchema,
  actorWasClueGiver: z.boolean(),
  marker: MarkerPlacementSchema.nullable(),
  previousIconId: IconIdSchema.nullable(),
  at: z.number().int().nonnegative(),
}).strict();
export type BoardActivity = z.infer<typeof BoardActivitySchema>;

export const PlayerViewSchema = z.object({
  id: PlayerIdSchema,
  nickname: NicknameSchema,
  score: z.number().int().nonnegative(),
  isHost: z.boolean(),
  isConnected: z.boolean(),
}).strict();

export const RoomViewSchema = z.object({
  code: RoomCodeSchema,
  mode: z.literal('freestyle'),
  phase: z.enum(['lobby', 'freestyle']),
  version: z.number().int().nonnegative(),
  hostPlayerId: PlayerIdSchema,
  players: z.array(PlayerViewSchema).max(12),
  activeClueGiverIds: z.array(PlayerIdSchema).max(12),
  board: z.array(MarkerPlacementSchema).max(47),
  conceptOrder: z.array(MarkerColorSchema).max(5),
  cubeOrder: z.array(MarkerIdSchema).max(42),
  boardActivity: z.array(BoardActivitySchema),
  roundNumber: z.number().int().nonnegative(),
}).strict();

export type RoomView = z.infer<typeof RoomViewSchema>;

export const PublicRoomSummarySchema = z.object({
  roomCode: RoomCodeSchema,
  name: z.string().min(1).max(28),
  playerCount: z.number().int().positive().max(12),
  isFull: z.boolean(),
}).strict();
export type PublicRoomSummary = z.infer<typeof PublicRoomSummarySchema>;

export const CreateRoomInputSchema = z.object({
  nickname: NicknameSchema,
  isPublic: z.boolean().optional(),
}).strict();
export const JoinRoomInputSchema = z.object({
  roomCode: RoomCodeSchema,
  nickname: NicknameSchema,
}).strict();
export const ListPublicRoomsInputSchema = z.object({}).strict();
export const LeaveRoomInputSchema = z.object({}).strict();
export const ResumeRoomInputSchema = z.object({
  roomCode: RoomCodeSchema,
  playerId: PlayerIdSchema,
  reconnectToken: ReconnectTokenSchema,
}).strict();
export const OpenBoardInputSchema = z.object({
  roomCode: RoomCodeSchema,
  commandId: CommandIdSchema,
  expectedVersion: z.number().int().nonnegative(),
}).strict();
export const ToggleClueGiverInputSchema = OpenBoardInputSchema;
export const UpdateNicknameInputSchema = OpenBoardInputSchema.extend({
  nickname: NicknameSchema,
}).strict();
const BoardCommandBaseSchema = OpenBoardInputSchema;
export const PlaceMarkerInputSchema = BoardCommandBaseSchema.extend({
  iconId: IconIdSchema,
  color: MarkerColorSchema,
  kind: MarkerKindSchema,
}).strict();
export const MoveMarkerInputSchema = BoardCommandBaseSchema.extend({
  markerId: MarkerIdSchema,
  iconId: IconIdSchema,
}).strict();
export const RemoveMarkerInputSchema = BoardCommandBaseSchema.extend({
  markerId: MarkerIdSchema,
}).strict();
export const ReorderOverviewInputSchema = z.discriminatedUnion('kind', [
  BoardCommandBaseSchema.extend({
    kind: z.literal('concepts'),
    colors: z.array(MarkerColorSchema).min(1).max(5),
  }).strict(),
  BoardCommandBaseSchema.extend({
    kind: z.literal('cubes'),
    color: MarkerColorSchema,
    markerIds: z.array(MarkerIdSchema).min(1).max(10),
  }).strict(),
]);
export const ClearBoardInputSchema = BoardCommandBaseSchema;
export const TvPrepareInputSchema = z.object({
  roomCode: RoomCodeSchema,
}).strict();
export const TvStatusInputSchema = z.object({ roomCode: RoomCodeSchema }).strict();
export const TvRouteStateInputSchema = z.object({
  roomCode: RoomCodeSchema,
  streamId: z.string().uuid(),
  isWireless: z.boolean(),
}).strict();
export const TvStopInputSchema = z.object({
  roomCode: RoomCodeSchema,
  streamId: z.string().uuid(),
}).strict();
export const TvSubscribeInputSchema = z.object({
  streamId: z.string().uuid(),
  rendererToken: z.string().min(32).max(256),
}).strict();
export const TvRendererReadyInputSchema = TvSubscribeInputSchema;

export const TvStreamStatusSchema = z.object({
  streamId: z.string().uuid(),
  roomCode: RoomCodeSchema,
  state: z.enum(['preparing', 'ready', 'stopped', 'error']),
  playbackUrl: z.string().url().nullable(),
  message: z.string().max(200).nullable(),
  expiresAt: z.number().int().positive(),
}).strict();
export type TvStreamStatus = z.infer<typeof TvStreamStatusSchema>;

export type CreateRoomInput = z.infer<typeof CreateRoomInputSchema>;
export type JoinRoomInput = z.infer<typeof JoinRoomInputSchema>;
export type ListPublicRoomsInput = z.infer<typeof ListPublicRoomsInputSchema>;
export type LeaveRoomInput = z.infer<typeof LeaveRoomInputSchema>;
export type ResumeRoomInput = z.infer<typeof ResumeRoomInputSchema>;
export type OpenBoardInput = z.infer<typeof OpenBoardInputSchema>;
export type ToggleClueGiverInput = z.infer<typeof ToggleClueGiverInputSchema>;
export type UpdateNicknameInput = z.infer<typeof UpdateNicknameInputSchema>;
export type PlaceMarkerInput = z.infer<typeof PlaceMarkerInputSchema>;
export type MoveMarkerInput = z.infer<typeof MoveMarkerInputSchema>;
export type RemoveMarkerInput = z.infer<typeof RemoveMarkerInputSchema>;
export type ReorderOverviewInput = z.infer<typeof ReorderOverviewInputSchema>;
export type ClearBoardInput = z.infer<typeof ClearBoardInputSchema>;
export type TvPrepareInput = z.infer<typeof TvPrepareInputSchema>;
export type TvStatusInput = z.infer<typeof TvStatusInputSchema>;
export type TvRouteStateInput = z.infer<typeof TvRouteStateInputSchema>;
export type TvStopInput = z.infer<typeof TvStopInputSchema>;
export type TvSubscribeInput = z.infer<typeof TvSubscribeInputSchema>;
export type TvRendererReadyInput = z.infer<typeof TvRendererReadyInputSchema>;

export const SessionCredentialsSchema = z.object({
  roomCode: RoomCodeSchema,
  playerId: PlayerIdSchema,
  reconnectToken: ReconnectTokenSchema,
}).strict();
export type SessionCredentials = z.infer<typeof SessionCredentialsSchema>;

export const SessionOutputSchema = z.object({
  credentials: SessionCredentialsSchema,
  room: RoomViewSchema,
}).strict();
export type SessionOutput = z.infer<typeof SessionOutputSchema>;

export const ProtocolErrorCodeSchema = z.enum([
  'INVALID_COMMAND', 'ROOM_NOT_FOUND', 'ROOM_FULL', 'NICKNAME_UNAVAILABLE',
  'NOT_AUTHORIZED', 'WRONG_PHASE', 'STALE_VERSION', 'NOT_ENOUGH_PLAYERS',
  'INVALID_SESSION',
  'MARKER_UNAVAILABLE', 'MARKER_NOT_FOUND',
  'TV_STREAM_BUSY', 'TV_STREAM_UNAVAILABLE', 'TV_STREAM_NOT_FOUND',
]);
export type ProtocolErrorCode = z.infer<typeof ProtocolErrorCodeSchema>;

export type CommandResult<T> =
  | { ok: true; value: T }
  | { ok: false; error: { code: ProtocolErrorCode; message: string } };
export type Ack<T> = (result: CommandResult<T>) => void;

export interface ClientToServerEvents {
  'room:create': (input: CreateRoomInput, ack: Ack<SessionOutput>) => void;
  'room:join': (input: JoinRoomInput, ack: Ack<SessionOutput>) => void;
  'room:list-public': (
    input: ListPublicRoomsInput,
    ack: Ack<{ rooms: PublicRoomSummary[] }>,
  ) => void;
  'room:leave': (input: LeaveRoomInput, ack: Ack<Record<string, never>>) => void;
  'room:resume': (input: ResumeRoomInput, ack: Ack<SessionOutput>) => void;
  'player:update-nickname': (
    input: UpdateNicknameInput,
    ack: Ack<{ room: RoomView }>,
  ) => void;
  'lobby:open-board': (input: OpenBoardInput, ack: Ack<{ room: RoomView }>) => void;
  'freestyle:toggle-clue-giver': (
    input: ToggleClueGiverInput,
    ack: Ack<{ room: RoomView }>,
  ) => void;
  'board:place-marker': (input: PlaceMarkerInput, ack: Ack<{ room: RoomView }>) => void;
  'board:move-marker': (input: MoveMarkerInput, ack: Ack<{ room: RoomView }>) => void;
  'board:remove-marker': (input: RemoveMarkerInput, ack: Ack<{ room: RoomView }>) => void;
  'board:reorder-overview': (input: ReorderOverviewInput, ack: Ack<{ room: RoomView }>) => void;
  'board:clear': (input: ClearBoardInput, ack: Ack<{ room: RoomView }>) => void;
  'tv:prepare': (input: TvPrepareInput, ack: Ack<{ stream: TvStreamStatus }>) => void;
  'tv:status': (input: TvStatusInput, ack: Ack<{ stream: TvStreamStatus | null }>) => void;
  'tv:route-state': (input: TvRouteStateInput, ack: Ack<{ stream: TvStreamStatus }>) => void;
  'tv:stop': (input: TvStopInput, ack: Ack<Record<string, never>>) => void;
  'tv:subscribe': (input: TvSubscribeInput, ack: Ack<{ room: RoomView }>) => void;
  'tv:renderer-ready': (input: TvRendererReadyInput, ack: Ack<Record<string, never>>) => void;
}
export interface ServerToClientEvents {
  'room:view': (view: RoomView) => void;
  'room:public-list': (rooms: PublicRoomSummary[]) => void;
  'room:closed': (reason: string) => void;
  'tv:stream-status': (status: TvStreamStatus) => void;
}
export interface InterServerEvents {}
export interface SocketData {
  roomCode?: string;
  playerId?: string;
  tvStreamId?: string;
}
